/**
 * O catálogo das integrações: quem se conecta e o que cada um pede.
 *
 * Fica fora dos adaptadores porque é a mesma lista nas duas pontas — a tela da
 * clínica desenha o formulário a partir daqui, e o banco recebe o jsonb com
 * exatamente estes campos.
 *
 * **Nenhum valor destes campos volta do servidor.** Eles entram por uma função
 * que os cifra no Vault; a tela lê apenas se existe credencial e quando ela
 * mudou. É por isso que o formulário nasce vazio mesmo num canal já conectado:
 * não há o que preencher de volta, e fingir que há seria mostrar um segredo que
 * ninguém leu.
 */

import type { CanalDeIntegracao, ProvedorDeIa, TipoDeCanal } from './tipos';

export interface CampoDeCredencial {
    chave: string;
    rotulo: string;
    /** `senha` some da tela ao ser digitado; `texto` fica à vista. */
    tipo: 'senha' | 'texto';
    ajuda?: string;
    opcional?: boolean;
}

export interface ProvedorDeCanal {
    id: string;
    nome: string;
    tipo: TipoDeCanal;
    descricao: string;
    /** O que a clínica precisa ter em mãos antes de começar. */
    prerequisito: string;
    campos: CampoDeCredencial[];
    documentacao: string;
}

export const PROVEDORES_DE_CANAL: ProvedorDeCanal[] = [
    {
        id: 'uazapi',
        nome: 'UAZAPI',
        tipo: 'whatsapp',
        descricao: 'WhatsApp por instância não oficial — o número comum da recepção, pareado por QR.',
        prerequisito: 'Uma instância criada no painel da UAZAPI, com token.',
        campos: [
            { chave: 'url', rotulo: 'URL do servidor', tipo: 'texto', ajuda: 'https://sua-instancia.uazapi.com' },
            { chave: 'instancia', rotulo: 'Nome da instância', tipo: 'texto' },
            { chave: 'token', rotulo: 'Token', tipo: 'senha' },
        ],
        documentacao: 'https://uazapi.com',
    },
    {
        id: 'waba',
        nome: 'WhatsApp Business API (oficial)',
        tipo: 'whatsapp',
        descricao: 'A API oficial da Meta. Exige número verificado e modelos de mensagem aprovados.',
        prerequisito: 'Um app no Meta for Developers, número verificado e token permanente.',
        campos: [
            { chave: 'phone_number_id', rotulo: 'Phone number ID', tipo: 'texto' },
            { chave: 'business_account_id', rotulo: 'WhatsApp Business Account ID', tipo: 'texto' },
            { chave: 'token', rotulo: 'Token permanente', tipo: 'senha' },
            { chave: 'verify_token', rotulo: 'Verify token do webhook', tipo: 'senha', opcional: true },
        ],
        documentacao: 'https://developers.facebook.com/docs/whatsapp',
    },
    {
        /* `meta`, e não `instagram`: `instagram` é o TIPO do canal, e o provedor
           é quem opera a API. Os dois valores existem na mesma linha do banco, e
           usar a mesma palavra para os dois já fez a tela de credencial abrir
           dizendo "provedor desconhecido" para o canal semeado. */
        id: 'meta',
        nome: 'Instagram Direct',
        tipo: 'instagram',
        descricao: 'Mensagens diretas do perfil comercial, pela Graph API da Meta.',
        prerequisito: 'Conta profissional ligada a uma página do Facebook, com token de página.',
        campos: [
            { chave: 'ig_user_id', rotulo: 'Instagram user ID', tipo: 'texto' },
            { chave: 'page_id', rotulo: 'ID da página', tipo: 'texto' },
            { chave: 'token', rotulo: 'Token da página', tipo: 'senha' },
        ],
        documentacao: 'https://developers.facebook.com/docs/messenger-platform/instagram',
    },
    {
        id: 'smtp',
        nome: 'E-mail (SMTP)',
        tipo: 'email',
        descricao: 'Envio pelo servidor de e-mail da própria clínica.',
        prerequisito: 'Host, porta e uma senha de aplicativo — a senha da conta não serve em provedor com 2FA.',
        campos: [
            { chave: 'host', rotulo: 'Servidor', tipo: 'texto', ajuda: 'smtp.gmail.com' },
            { chave: 'porta', rotulo: 'Porta', tipo: 'texto', ajuda: '587' },
            { chave: 'usuario', rotulo: 'Usuário', tipo: 'texto' },
            { chave: 'senha', rotulo: 'Senha de aplicativo', tipo: 'senha' },
        ],
        documentacao: 'https://support.google.com/mail/answer/185833',
    },
];

export function provedorDeCanal(id: string): ProvedorDeCanal | undefined {
    return PROVEDORES_DE_CANAL.find((p) => p.id === id);
}

export interface DescricaoDeProvedorDeIa {
    id: ProvedorDeIa;
    nome: string;
    /** Modelos que costumam ser escolhidos; a caixa aceita qualquer outro. */
    modelos: string[];
    padrao: string;
    documentacao: string;
    /** Como a chave costuma começar — ajuda a perceber que se colou a errada. */
    prefixo?: string;
}

export const PROVEDORES_DE_IA: DescricaoDeProvedorDeIa[] = [
    {
        id: 'anthropic',
        nome: 'Anthropic (Claude)',
        modelos: ['claude-opus-5', 'claude-sonnet-5', 'claude-haiku-4-5-20251001'],
        padrao: 'claude-sonnet-5',
        documentacao: 'https://docs.anthropic.com',
        prefixo: 'sk-ant-',
    },
    {
        id: 'openai',
        nome: 'OpenAI',
        modelos: ['gpt-4.1', 'gpt-4.1-mini', 'o4-mini'],
        padrao: 'gpt-4.1-mini',
        documentacao: 'https://platform.openai.com/docs',
        prefixo: 'sk-',
    },
    {
        id: 'gemini',
        nome: 'Google Gemini',
        /* A família 2.5 responde 404 ("no longer available to new users") para chave nova
           do Google AI Studio, mas continua valendo para chave antiga — e fica na lista
           para quem já a usa. O padrão é o do app original, bom custo e resposta rápida;
           o `3.1-flash-lite` sai do ar em 07/05/2027 e o substituto é o `3.5-flash-lite`
           (ai.google.dev, "Models" e "Deprecations", 13/09/2026). */
        modelos: ['gemini-3.1-flash-lite', 'gemini-3.5-flash-lite', 'gemini-3.5-flash', 'gemini-2.5-flash', 'gemini-3.1-pro-preview'],
        padrao: 'gemini-3.1-flash-lite',
        documentacao: 'https://ai.google.dev',
    },
    {
        id: 'grok',
        nome: 'xAI (Grok)',
        modelos: ['grok-4', 'grok-3-mini'],
        padrao: 'grok-4',
        documentacao: 'https://docs.x.ai',
        prefixo: 'xai-',
    },
    {
        id: 'openrouter',
        nome: 'OpenRouter',
        /* Porta única para modelos de vários donos: o id leva o dono na frente. O catálogo
           de lá muda toda semana, então isto é sugestão — a caixa aceita qualquer id, e a
           lista inteira está em openrouter.ai/models. */
        modelos: ['openai/gpt-4o', 'anthropic/claude-sonnet-5', 'google/gemini-3.5-flash'],
        padrao: 'openai/gpt-4o',
        documentacao: 'https://openrouter.ai/docs',
        prefixo: 'sk-or-',
    },
];

export function provedorDeIa(id: string): DescricaoDeProvedorDeIa | undefined {
    return PROVEDORES_DE_IA.find((p) => p.id === id);
}

/**
 * Por que a VecchIA ainda não fala com este provedor — nulo quando fala.
 *
 * O formulário de credencial existe para os quatro, e é bom que exista: a
 * clínica guarda a credencial e ela está pronta quando a integração chegar. O
 * que não pode é o canal ficar "aguardando o provedor" e a mensagem "pendente"
 * para sempre, porque nenhum código do lado de cá conversa com a Meta nem com
 * um servidor SMTP. Hoje só a UAZAPI envia e recebe.
 */
const INDISPONIVEL: Record<string, string> = {
    waba: 'A Fórmula MED ainda não fala com a API oficial do WhatsApp: a credencial fica guardada, mas este canal não envia nem recebe. Responda pelo WhatsApp Business da clínica.',
    meta: 'A Fórmula MED ainda não fala com o Instagram Direct: a credencial fica guardada, mas este canal não envia nem recebe. Responda pelo aplicativo do Instagram.',
    smtp: 'A Fórmula MED ainda não envia pelo e-mail da clínica: a credencial fica guardada, mas este canal não envia nem recebe. Responda pelo seu programa de e-mail.',
};

export function indisponibilidadeDoProvedor(provedor: string): string | null {
    return INDISPONIVEL[provedor] ?? null;
}

/**
 * O estado de um canal em palavras.
 *
 * Ter credencial não é ter sessão: o provedor confirma a conexão no primeiro
 * webhook, e até lá o canal está "conectando". Dizer "conectado" só porque
 * alguém colou um token faria a clínica contar com um envio que não sai.
 */
export function estadoDoCanal(canal: CanalDeIntegracao): { rotulo: string; tom: 'success' | 'warning' | 'danger' | 'neutral' } {
    /* O e-mail já se conecta — o servidor testa o login SMTP —, mas ainda não
       envia pelas conversas (17/09/2026). */
    if (canal.provedor === 'smtp') {
        if (canal.status === 'conectado') return { rotulo: 'Login testado · envio ainda não disponível', tom: 'success' };
        if (canal.status === 'erro') return { rotulo: 'Erro no login', tom: 'danger' };
        if (canal.temCredencial) return { rotulo: 'Credencial salva, sem teste', tom: 'warning' };
        return { rotulo: 'Sem credencial', tom: 'neutral' };
    }
    /* Antes de tudo: provedor com o qual a VecchIA ainda não fala não fica
       "aguardando o provedor" — não há quem responda do outro lado. */
    if (indisponibilidadeDoProvedor(canal.provedor)) return { rotulo: 'Integração ainda não disponível', tom: 'neutral' };
    if (canal.status === 'conectado') return { rotulo: 'Conectado', tom: 'success' };
    if (canal.status === 'erro') return { rotulo: canal.ultimoErro ? 'Erro' : 'Erro no provedor', tom: 'danger' };
    if (canal.temCredencial) return { rotulo: 'Credencial salva, aguardando o provedor', tom: 'warning' };
    return { rotulo: 'Sem credencial', tom: 'neutral' };
}

/**
 * O QR que a demonstração mostra.
 *
 * Ele **não pareia nada**: não há WhatsApp do outro lado de uma instalação de
 * demonstração. Por isso a imagem diz "DEMONSTRAÇÃO" no meio, em vez de imitar
 * um código válido — um QR falso que parece verdadeiro faz alguém apontar o
 * telefone e concluir que o produto está quebrado.
 */
export const QR_DE_DEMONSTRACAO = 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22240%22%20height%3D%22240%22%20viewBox%3D%220%200%20240%20240%22%20role%3D%22img%22%20aria-label%3D%22QR%20Code%20de%20demonstracao%22%3E%20%3Crect%20width%3D%22240%22%20height%3D%22240%22%20fill%3D%22%23ffffff%22%2F%3E%20%3Cg%20fill%3D%22%23111827%22%3E%20%3Cpath%20d%3D%22M16%2016h56v56H16zm8%208v40h40V24z%22%2F%3E%3Crect%20x%3D%2232%22%20y%3D%2232%22%20width%3D%2224%22%20height%3D%2224%22%2F%3E%20%3Cpath%20d%3D%22M168%2016h56v56h-56zm8%208v40h40V24z%22%2F%3E%3Crect%20x%3D%22184%22%20y%3D%2232%22%20width%3D%2224%22%20height%3D%2224%22%2F%3E%20%3Cpath%20d%3D%22M16%20168h56v56H16zm8%208v40h40v-40z%22%2F%3E%3Crect%20x%3D%2232%22%20y%3D%22184%22%20width%3D%2224%22%20height%3D%2224%22%2F%3E%20%3Crect%20x%3D%2288%22%20y%3D%2216%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22104%22%20y%3D%2216%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22136%22%20y%3D%2216%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%2288%22%20y%3D%2232%22%20width%3D%228%22%20height%3D%2216%22%2F%3E%3Crect%20x%3D%22112%22%20y%3D%2232%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22144%22%20y%3D%2240%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%2296%22%20y%3D%2256%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22120%22%20y%3D%2256%22%20width%3D%228%22%20height%3D%2216%22%2F%3E%3Crect%20x%3D%22136%22%20y%3D%2264%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%2216%22%20y%3D%2288%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%2232%22%20y%3D%2288%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%2256%22%20y%3D%2296%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%2216%22%20y%3D%22112%22%20width%3D%228%22%20height%3D%2216%22%2F%3E%3Crect%20x%3D%2240%22%20y%3D%22120%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%2264%22%20y%3D%22112%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%2216%22%20y%3D%22144%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%2248%22%20y%3D%22144%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%22168%22%20y%3D%2288%22%20width%3D%228%22%20height%3D%2216%22%2F%3E%3Crect%20x%3D%22192%22%20y%3D%2288%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22216%22%20y%3D%2296%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%22176%22%20y%3D%22112%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22200%22%20y%3D%22120%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22168%22%20y%3D%22136%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%22192%22%20y%3D%22144%22%20width%3D%228%22%20height%3D%2216%22%2F%3E%3Crect%20x%3D%22216%22%20y%3D%22152%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%2288%22%20y%3D%22168%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22112%22%20y%3D%22176%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22144%22%20y%3D%22168%22%20width%3D%228%22%20height%3D%2216%22%2F%3E%20%3Crect%20x%3D%2288%22%20y%3D%22192%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22120%22%20y%3D%22200%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22152%22%20y%3D%22192%22%20width%3D%228%22%20height%3D%228%22%2F%3E%20%3Crect%20x%3D%2296%22%20y%3D%22216%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22128%22%20y%3D%22216%22%20width%3D%2216%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22168%22%20y%3D%22200%22%20width%3D%228%22%20height%3D%2216%22%2F%3E%20%3Crect%20x%3D%22192%22%20y%3D%22184%22%20width%3D%228%22%20height%3D%228%22%2F%3E%3Crect%20x%3D%22216%22%20y%3D%22200%22%20width%3D%228%22%20height%3D%2216%22%2F%3E%20%3Crect%20x%3D%22104%22%20y%3D%2296%22%20width%3D%2232%22%20height%3D%2232%22%20opacity%3D%22.15%22%2F%3E%20%3C%2Fg%3E%20%3Crect%20x%3D%2252%22%20y%3D%22104%22%20width%3D%22136%22%20height%3D%2232%22%20rx%3D%226%22%20fill%3D%22%2370142D%22%2F%3E%20%3Ctext%20x%3D%22120%22%20y%3D%22125%22%20font-family%3D%22system-ui%2Csans-serif%22%20font-size%3D%2213%22%20font-weight%3D%22600%22%20fill%3D%22%23ffffff%22%20text-anchor%3D%22middle%22%3EDEMONSTRA%C3%87%C3%83O%3C%2Ftext%3E%20%3C%2Fsvg%3E';
